import React, { useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ModalAction from '@/components/ModalAction';
import CheckIcon from '@/images/icons/Check';
import LoaderIcon from '@/images/icons/Loader';
import CrossIcon from '@/images/icons/Cross';
import { fetchAPI } from '@/api';
import { AuthState } from '@/types';
import { Input } from '@/components/Form/Input';
import { setCurrentProjectId } from '@/actions';

const DeleteModal = ({ modalOpen, setModalOpen, modalMessage, modalError, setModalMessage, setModalError, isConfirmed, setIsConfirmed, projectId }: any) => {
	const auth = useSelector((state: { auth: AuthState }) => state.auth);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [deleteInput, setDeleteInput] = useState('');

	const validatePhrase = "YES, DELETE MY PROJECT";

	const onClearModal = () => {
		setModalOpen(false);
		setTimeout(() => {
			setModalMessage(null);
			setModalError(null);
			setIsConfirmed(false);
		}, 400);
	};

	const onDeleteProject = async () => {
		if (deleteInput !== validatePhrase) {
			setModalError('Please type the correct phrase to delete the project');
			return;
		}

		setModalError(null);
		setIsConfirmed(true);

		try {
			const response = await fetchAPI("/project/delete", auth?.user?.token, {
				projectId,
			});
			if (!response.ok) {
				throw new Error(`HTTP error: ${response.status}`);
			}

			const data = await response.json();

			if (data) {
				setModalMessage("Project deleted successfully");
				setTimeout(() => {
					dispatch(setCurrentProjectId(null));
					navigate('/select_project');
				}, 2000);
			}
		} catch (error: any) {
			console.error('Error fetching data:', error);
			let errorMessage = 'An unexpected error occurred';
			if (error.response?.message) {
				errorMessage = error.response.message;
			}
			setModalError(errorMessage);
		}
	};

	return (
		<ModalAction
			id="cancel-modal"
			modalOpen={modalOpen}
			setModalOpen={setModalOpen}
			onModalClose={() => onClearModal()}
			size={undefined}
		>
			{modalMessage ? (
				<h2 className="h2 mb-4 pr-16 md:pr-20 leading-[0.9]">
					{modalMessage}
				</h2>
			) : (
				<>
					<div className="h2 mb-4">
						Delete project
					</div>
					<div className="mb-1">If you really want to delete your project, please type (exactly):</div>
					<div className="text-sm text-red-orange font-medium">
						{validatePhrase}
					</div>
					<Input className="h-11 mt-6"
					       type="text"
					       name="projectDelete"
					       placeholder="Confirmation text"
					       defaultValue={deleteInput}
					       onChange={(e) => setDeleteInput(e.target.value)}
					       required>
					</Input>
				</>
			)}
			<button className={classNames(
				'btn-primary w-full mt-8',
				!isConfirmed && 'hidden',
			)} onClick={() => onClearModal()}>
				{(!modalMessage && !modalError) && (
					<div className="flex items-center justify-center py-1 pointer-events-none">
						<LoaderIcon className="animate-rotation w-6 h-6 text-white" />
					</div>
				)}
				{modalMessage && (
					<div className="flex items-center justify-center py-1 pointer-events-none">
						<CheckIcon className="w-6 h-5 text-white" />
					</div>
				)}
				{modalError && (
					<div className="flex items-center justify-center py-1 pointer-events-none">
						<CrossIcon className="w-5 h-5 text-white" />
					</div>
				)}
			</button>
			<div className={classNames(
				isConfirmed && 'hidden',
			)}>
				<button className="btn-primary w-full mt-8" onClick={() => onDeleteProject()}>
					<span className="pointer-events-none">Delete</span>
				</button>
				<button className="btn-primary-inverted w-full mt-4" onClick={() => onClearModal()}>
					<span className="pointer-events-none">Save project</span>
				</button>
			</div>
			{modalError && (
				<div className="text-sm text-red-orange mt-2 font-medium">{modalError}</div>
			)}
		</ModalAction>
	);
};

export default DeleteModal;
